import { Injectable } from '@angular/core';
import { TranslateLoader } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class CustomTranslationLoader implements TranslateLoader {
  constructor(private http: HttpClient) {}

  getTranslation(lang: string): Observable<any> {
    if (lang === 'en') {
      return this.loadStaticTranslationFile('en');
    }

    const cachedTranslations = this.getCachedTranslation(lang);

    if (cachedTranslations) {
      return of(cachedTranslations);
    }

    const serverUrl = localStorage.getItem('server_url');

    if (!serverUrl) {
      return this.loadStaticTranslationFile(lang);
    }

    const apiURL =
      serverUrl +
      'b/system/v3/debtor_api/get_all_translations?source=debtor_API';

    return this.http.get(apiURL).pipe(
      map((response: any) => this.processConfigLanguages(response, lang)),
      catchError((error) => {
        return this.loadStaticTranslationFile(lang);
      })
    );
  }

  private loadStaticTranslationFile(lang: string): Observable<any> {
    const staticTranslationsPath = `./assets/i18n/${lang}.json`;

    return new Observable((observer) => {
      fetch(staticTranslationsPath)
        .then((response) => response.json())
        .then((translations) => {
          observer.next(translations);
          observer.complete();
        })
        .catch((error) => {
          observer.next({});
          observer.complete();
        });
    });
  }

  clearCache() {
    localStorage.removeItem('all_translations');
    localStorage.removeItem('all_translations_timestamp');
    localStorage.removeItem('available_languages');
    localStorage.removeItem('language_names');
  }

  getAvailableLanguages(): string[] {
    try {
      const cached = localStorage.getItem('available_languages');
      const backendLanguages = cached ? JSON.parse(cached) : [];

      return ['en', ...backendLanguages.filter((lang) => lang !== 'en')];
    }
    catch {
      return ['en'];
    }
  }

  getLanguageNames(): { [key: string]: string } {
    try {
      const cached = localStorage.getItem('language_names');
      const backendNames = cached ? JSON.parse(cached) : {};

      return { en: 'English', ...backendNames };
    }
    catch {
      return { en: 'English' };
    }
  }

  private getCachedTranslation(lang: string): any | null {
    try {
      const cached = localStorage.getItem('all_translations');
      const cacheTime = localStorage.getItem('all_translations_timestamp');

      if (!cached || !cacheTime) return null;
      if (Date.now() - parseInt(cacheTime) >= 24 * 60 * 60 * 1000) return null;

      const parsed = JSON.parse(cached);
      const langTranslations = parsed[lang];

      return langTranslations &&
        typeof langTranslations === 'object' &&
        Object.keys(langTranslations).length > 0
        ? langTranslations
        : null;
    }
    catch {
      return null;
    }
  }

  private processConfigLanguages(response: any, requestedLang: string): any {
    if (!response || typeof response !== 'object') return {};

    const availableLanguages = Object.keys(response).filter(
      (key) =>
        key !== 'translations' &&
        typeof response[key] === 'object' &&
        response[key] !== null &&
        Object.keys(response[key]).length > 0
    );

    if (availableLanguages.length > 0) {
      localStorage.setItem('all_translations', JSON.stringify(response));
      localStorage.setItem('all_translations_timestamp', Date.now().toString());
      localStorage.setItem(
        'available_languages',
        JSON.stringify(availableLanguages)
      );

      if (response.translations) {
        localStorage.setItem(
          'language_names',
          JSON.stringify(response.translations)
        );
      }
    }

    return response[requestedLang] || {};
  }
}
